import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import IndexApp from "../../Components/IndexApp";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { DV2 } from "../DeclarationV2/theme";
import { plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { useDv2Theme } from "../DeclarationV2/layout/Ui";
import ClientCourseTableV2 from "./ClientCourseTableV2";
import { CLIENT_COURSES_TAB_PARAM, ClientCoursesTab, isClientCoursesTab } from "./clientPaths";

/** Type de liste du hook `useClientListCourse` pour chaque onglet. */
const TAB_TYPE: Record<ClientCoursesTab, "avenir" | "historique"> = { "a-venir": "avenir", passes: "historique" };
/** Ancien état de navigation (`location.state`) de l'accueil : à venir / historique. */
const TAB_FROM_LEGACY: Record<string, ClientCoursesTab> = { avenir: "a-venir", historique: "passes" };

/**
 * « Mes cours » du client repensé (espace client, 2 novembre 2026) : titre
 * et sous-titre chiffré, deux onglets portés par l'URL (`?onglet=a-venir|passes`),
 * tableau `ClientCourseTableV2`. L'ancien écran (`ClientCourses`,
 * `ClientListCourse`) reste dans le code.
 */
const ClientCoursesV2 = () => {
    const client = useSelector((state: any) => state?.user?.currentUser?.client);
    const { primary, fontFamily, text } = useDv2Theme();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [total, setTotal] = useState<number | null>(null);

    const legacy = typeof location.state === "string" ? TAB_FROM_LEGACY[location.state] : (location.state as any)?.tab;
    const paramTab = searchParams.get(CLIENT_COURSES_TAB_PARAM);
    const tab: ClientCoursesTab = isClientCoursesTab(paramTab) ? paramTab : (isClientCoursesTab(legacy) ? legacy : "a-venir");
    useEffect(() => {
        if (!isClientCoursesTab(paramTab) && isClientCoursesTab(legacy)) {
            const next = new URLSearchParams(searchParams);
            next.set(CLIENT_COURSES_TAB_PARAM, legacy);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectTab = (next: ClientCoursesTab) => {
        if (next === tab) return;
        const params = new URLSearchParams(searchParams);
        params.set(CLIENT_COURSES_TAB_PARAM, next);
        setSearchParams(params, { replace: true });
        setTotal(null);
    };

    const aiciActive = !!client?.idClientTiersPrestation;
    const tabs: Array<{ id: ClientCoursesTab; label: string }> = [
        { id: "a-venir", label: "À venir" },
        { id: "passes", label: "Passés" },
    ];
    const subtitle = total === null
        ? `Les ${vocab.courses()} déclarés par ${vocab.yourTeachr()}, à venir comme passés.`
        : tab === "a-venir"
            ? (total === 0 ? `Aucun ${vocab.course()} à venir pour le moment.` : `${capitalizeFirstLetter(plural(total, vocab.course(), vocab.courses()))} à venir, déclaré${total > 1 ? "s" : ""} par ${vocab.yourTeachr()}.`)
            : (total === 0 ? `Aucun ${vocab.course()} passé pour le moment.` : `${capitalizeFirstLetter(plural(total, vocab.course(), vocab.courses()))} passé${total > 1 ? "s" : ""}, avec leur statut URSSAF.`);

    return (
        <IndexApp
            navBar={{ index: 1 }}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <div className="mx-auto w-full px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                        <header>
                            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Mes {vocab.courses()}</h1>
                            <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle}</div>
                        </header>

                        <div role="tablist" aria-label={`Onglets de mes ${vocab.courses()}`} className="flex gap-1 overflow-x-auto" style={{ borderBottom: `1px solid ${DV2.border}` }}>
                            {tabs.map((t) => {
                                const selected = t.id === tab;
                                return (
                                    <button key={t.id} type="button" role="tab" aria-selected={selected} onClick={() => selectTab(t.id)} style={{ fontFamily, fontSize: 15, fontWeight: 800, padding: "10px 16px", background: "none", border: "none", borderBottom: `3px solid ${selected ? primary : "transparent"}`, color: selected ? primary : DV2.muted, cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap" }}>
                                        {t.label}{selected && total !== null ? `, ${total}` : ""}
                                    </button>
                                );
                            })}
                        </div>

                        <div key={tab}>
                            <ClientCourseTableV2 type={TAB_TYPE[tab]} aiciActive={aiciActive} clientId={client?.id} onTotal={setTotal} />
                        </div>

                        <p style={{ fontSize: 12, color: DV2.muted, margin: 0, lineHeight: 1.5 }}>
                            {aiciActive
                                ? `« Vous payez » est la moitié du prix ${vocab.ofTheCourse()} : l'autre moitié est réglée par l'URSSAF grâce à l'avance immédiate. Un ${vocab.course()} déclaré vous est présenté par l'URSSAF, vous avez 48 heures pour le valider ou le contester.`
                                : `Le prix affiché est le prix total TTC ${vocab.ofTheCourse()}. Activez l'avance immédiate dans vos paramètres pour n'en payer que la moitié.`}
                        </p>
                    </div>
                </main>
            }
        />
    );
};

export default ClientCoursesV2;
